import type { FastfoodItem } from '@gramas/shared'
import {
  Button,
  EmptyState,
  Field,
  List,
  ListRow,
  NumberField,
  SectionLabel,
  Segmented,
  Sheet,
  Spinner,
  Switch,
} from '@gramas/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../api/client.js'
import { type AdminLine, describeLine } from './adminLines.js'

interface Chain {
  id: number
  slug: string
  name: string
}

interface Component {
  id: number
  chainId: number
  name: string
  category: string | null
  unitWeightG: number | null
  isActive: boolean
  kcal: number
  protein: number
  carbs: number
  fat: number
  sugars: number | null
  satFat: number | null
  fiber: number | null
  salt: number | null
}

type Tab = 'components' | 'items' | 'lines'

/**
 * Filling the gaps a scraper cannot.
 *
 * Two of the chains publish a menu item's total but no figures for the parts
 * it is assembled from, so the builder has a bun it cannot price. This is where
 * those parts get their numbers, and where a chain nobody scrapes at all —
 * Narvesen, a bakery counter — gets entered by hand.
 */
export function Admin() {
  const [tab, setTab] = useState<Tab>('components')
  const [chainId, setChainId] = useState<number | null>(null)
  const [editing, setEditing] = useState<Component | 'new' | null>(null)
  const [editingItem, setEditingItem] = useState<FastfoodItem | null>(null)

  const chains = useQuery({
    queryKey: ['fastfood', 'chains'],
    queryFn: () => api.get<{ chains: Chain[] }>('/api/fastfood/chains').then((r) => r.chains),
  })

  const activeChain = chainId ?? chains.data?.[0]?.id ?? null

  const components = useQuery({
    queryKey: ['admin', 'components', activeChain],
    queryFn: () =>
      api
        .get<{ components: Component[] }>(
          `/api/fastfood/components?chainId=${activeChain}&includeInactive=1`,
        )
        .then((r) => r.components),
    enabled: activeChain != null,
  })

  const items = useQuery({
    queryKey: ['admin', 'items', activeChain],
    queryFn: () =>
      api
        .get<{ items: FastfoodItem[] }>(
          `/api/fastfood/items?chainId=${activeChain}&includeInactive=1`,
        )
        .then((r) => r.items),
    enabled: activeChain != null && tab === 'items',
  })

  if (chains.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    )
  }

  const missingWeight = (components.data ?? []).filter((c) => c.unitWeightG == null).length

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 flex-col gap-2 border-b border-line px-3 py-2">
        {tab !== 'lines' && (
          <select
            value={activeChain ?? ''}
            onChange={(e) => setChainId(Number(e.target.value))}
            className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-2 text-base text-ink"
          >
            {(chains.data ?? []).map((chain) => (
              <option key={chain.id} value={chain.id}>
                {chain.name}
              </option>
            ))}
          </select>
        )}
        <Segmented
          label="Skiltis"
          value={tab}
          onValueChange={(value) => setTab(value as Tab)}
          className="w-full"
          options={[
            { value: 'components', label: 'Sudedamosios' },
            { value: 'items', label: 'Patiekalai' },
            { value: 'lines', label: 'Glitimė' },
          ]}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {tab === 'lines' ? (
          <GlitimeLines />
        ) : tab === 'components' ? (
          <>
            <div className="flex items-center justify-between px-4 pb-1 pt-4">
              <span className="text-xs font-semibold uppercase tracking-[0.06em] text-mute">
                {(components.data ?? []).length} sudedamosios
                {missingWeight > 0 ? ` · ${missingWeight} be svorio` : ''}
              </span>
              <Button size="sm" onClick={() => setEditing('new')}>
                Nauja
              </Button>
            </div>
            {components.isLoading && (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            )}
            <List className="border-y border-line bg-surface">
              {(components.data ?? []).map((component, i) => (
                <ListRow
                  key={component.id}
                  last={i === (components.data ?? []).length - 1}
                  title={component.name}
                  subtitle={describeComponent(component)}
                  value={`${Math.round(component.kcal)}`}
                  meta="kcal/100g"
                  onActivate={() => setEditing(component)}
                />
              ))}
            </List>
          </>
        ) : (
          <>
            {items.isLoading && (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            )}
            {!items.isLoading && (items.data ?? []).length === 0 && (
              <EmptyState
                title="Patiekalų nėra"
                hint="Patiekalus sukuria mėnesinis duomenų surinkimas."
              />
            )}
            <List className="border-y border-line bg-surface">
              {(items.data ?? []).map((item, i) => (
                <ListRow
                  key={item.id}
                  last={i === (items.data ?? []).length - 1}
                  title={item.name}
                  subtitle={`${item.components.length} dalys${
                    item.sizeLabel ? ` · ${item.sizeLabel}` : ''
                  }`}
                  value={item.portionWeightG == null ? '—' : `${Math.round(item.portionWeightG)}`}
                  meta="g"
                  onActivate={() => setEditingItem(item)}
                />
              ))}
            </List>
          </>
        )}
        <div className="h-8" />
      </div>

      <ComponentSheet
        chainId={activeChain}
        component={editing === 'new' ? null : editing}
        open={editing != null}
        onOpenChange={(open) => !open && setEditing(null)}
      />

      <ItemComponentsSheet
        item={editingItem}
        components={components.data ?? []}
        onOpenChange={(open) => !open && setEditingItem(null)}
      />
    </div>
  )
}

/**
 * Read-only-ish window onto the nightly line generator's output — every line
 * currently live for every user, with a way to pull one before its
 * `expiresAt` if it turns out wrong. Generation itself lives behind the
 * admin-token-gated endpoints on the same route; this screen only lists and
 * deletes.
 */
function GlitimeLines() {
  const queryClient = useQueryClient()

  const lines = useQuery({
    queryKey: ['admin', 'glitime-lines'],
    queryFn: () => api.get<{ lines: AdminLine[] }>('/api/glitime/admin/lines').then((r) => r.lines),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/glitime/admin/lines/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'glitime-lines'] })
    },
  })

  if (lines.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }

  if (lines.isError) {
    return <div className="px-4 py-8 text-center text-sm text-danger">Nepavyko įkelti eilučių.</div>
  }

  if ((lines.data ?? []).length === 0) {
    return <EmptyState title="Eilučių nėra" hint="Jas sukuria naktinis eilučių generatorius." />
  }

  return (
    <>
      <div className="flex items-center justify-between px-4 pb-1 pt-4">
        <span className="text-xs font-semibold uppercase tracking-[0.06em] text-mute">
          {(lines.data ?? []).length} eilutės
        </span>
      </div>
      <List className="border-y border-line bg-surface">
        {(lines.data ?? []).map((line, i) => (
          <ListRow
            key={line.id}
            last={i === (lines.data ?? []).length - 1}
            title={
              line.surface.startsWith('push.') && line.title ? (
                <>
                  <strong>{line.title}: </strong>
                  {line.text}
                </>
              ) : (
                line.text
              )
            }
            subtitle={describeLine(line)}
            accessory={
              <button
                type="button"
                className="text-sm text-danger"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm('Pašalinti šią eilutę?')) remove.mutate(line.id)
                }}
              >
                Šalinti
              </button>
            }
          />
        ))}
      </List>
    </>
  )
}

function describeComponent(component: Component): string {
  const parts: string[] = []
  parts.push(component.unitWeightG == null ? 'svoris nenurodytas' : `${component.unitWeightG} g`)
  if (component.category) parts.push(component.category)
  if (!component.isActive) parts.push('neaktyvi')
  return parts.join(' · ')
}

interface ComponentValues {
  name: string
  category: string
  unitWeightG: number | null
  isActive: boolean
  kcal: number | null
  protein: number | null
  carbs: number | null
  fat: number | null
  sugars: number | null
  satFat: number | null
  fiber: number | null
  salt: number | null
}

const EMPTY_COMPONENT: ComponentValues = {
  name: '',
  category: '',
  unitWeightG: null,
  isActive: true,
  kcal: null,
  protein: null,
  carbs: null,
  fat: null,
  sugars: null,
  satFat: null,
  fiber: null,
  salt: null,
}

function ComponentSheet({
  chainId,
  component,
  open,
  onOpenChange,
}: {
  chainId: number | null
  component: Component | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [values, setValues] = useState<ComponentValues>(EMPTY_COMPONENT)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setValues(
      component
        ? {
            name: component.name,
            category: component.category ?? '',
            unitWeightG: component.unitWeightG,
            isActive: component.isActive,
            kcal: component.kcal,
            protein: component.protein,
            carbs: component.carbs,
            fat: component.fat,
            sugars: component.sugars,
            satFat: component.satFat,
            fiber: component.fiber,
            salt: component.salt,
          }
        : EMPTY_COMPONENT,
    )
  }, [open, component])

  const set = <K extends keyof ComponentValues>(key: K, value: ComponentValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }))

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        chainId,
        name: values.name.trim(),
        category: values.category.trim() || null,
        unitWeightG: values.unitWeightG,
        isActive: values.isActive,
        kcal: values.kcal ?? 0,
        protein: values.protein ?? 0,
        carbs: values.carbs ?? 0,
        fat: values.fat ?? 0,
        sugars: values.sugars,
        satFat: values.satFat,
        fiber: values.fiber,
        salt: values.salt,
      }
      return component
        ? api.put(`/api/fastfood/components/${component.id}`, body)
        : api.post('/api/fastfood/components', body)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'components'] })
      await queryClient.invalidateQueries({ queryKey: ['fastfood'] })
      onOpenChange(false)
    },
    onError: (e: Error) => setError(e.message),
  })

  const complete =
    values.name.trim().length > 0 &&
    values.kcal != null &&
    values.protein != null &&
    values.carbs != null &&
    values.fat != null &&
    (!values.isActive || values.unitWeightG != null)

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={component ? 'Taisyti sudedamąją' : 'Nauja sudedamoji'}
      description="Reikšmės 100 g"
      footer={
        <Button
          variant="primary"
          block
          size="lg"
          disabled={!complete || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saugoma…' : 'Išsaugoti'}
        </Button>
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        <Field
          label="Pavadinimas"
          value={values.name}
          onChange={(e) => set('name', e.target.value)}
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Kategorija"
            value={values.category}
            onChange={(e) => set('category', e.target.value)}
            placeholder="padažas, bandelė…"
          />
          <NumberField
            label="Vieneto svoris"
            suffix="g"
            hint="Būtinas aktyviai daliai"
            value={values.unitWeightG}
            onChange={(v) => set('unitWeightG', v)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberField
            label="Energija"
            suffix="kcal"
            value={values.kcal}
            onChange={(v) => set('kcal', v)}
          />
          <NumberField
            label="Baltymai"
            suffix="g"
            value={values.protein}
            onChange={(v) => set('protein', v)}
          />
          <NumberField
            label="Angliavandeniai"
            suffix="g"
            value={values.carbs}
            onChange={(v) => set('carbs', v)}
          />
          <NumberField
            label="Riebalai"
            suffix="g"
            value={values.fat}
            onChange={(v) => set('fat', v)}
          />
          <NumberField
            label="iš kurių cukrūs"
            suffix="g"
            value={values.sugars}
            onChange={(v) => set('sugars', v)}
          />
          <NumberField
            label="Sočiosios r. r."
            suffix="g"
            value={values.satFat}
            onChange={(v) => set('satFat', v)}
          />
          <NumberField
            label="Skaidulos"
            suffix="g"
            value={values.fiber}
            onChange={(v) => set('fiber', v)}
          />
          <NumberField
            label="Druska"
            suffix="g"
            value={values.salt}
            onChange={(v) => set('salt', v)}
          />
        </div>
        <Switch
          label="Rodoma rinkiklyje"
          checked={values.isActive}
          onCheckedChange={(checked) => set('isActive', checked)}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Sheet>
  )
}

interface Row {
  componentId: number
  weightG: number | null
  mandatory: boolean
  isDefault: boolean
  choiceGroup: string
  position: number
}

/**
 * Which parts an item is made of, and which of them the counter will swap.
 *
 * A shared `choiceGroup` is what turns three sauces into one question with one
 * answer, so it is the field that actually makes the builder useful; the rest
 * of the item's own fields are edited by the scraper, not here.
 */
function ItemComponentsSheet({
  item,
  components,
  onOpenChange,
}: {
  item: FastfoodItem | null
  components: readonly Component[]
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [rows, setRows] = useState<Row[]>([])
  const [adding, setAdding] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!item) return
    setError(null)
    setRows(
      item.components.map((c) => ({
        componentId: c.componentId,
        weightG: c.weightG,
        mandatory: c.mandatory,
        isDefault: c.isDefault,
        choiceGroup: c.choiceGroup ?? '',
        position: c.position,
      })),
    )
  }, [item])

  const nameOf = (componentId: number) =>
    components.find((c) => c.id === componentId)?.name ??
    item?.components.find((c) => c.componentId === componentId)?.name ??
    `#${componentId}`

  const save = useMutation({
    mutationFn: async () => {
      if (!item) return
      return api.put(`/api/fastfood/items/${item.id}`, {
        chainId: item.chainId,
        name: item.name,
        category: item.category,
        groupKey: item.groupKey,
        sizeLabel: item.sizeLabel,
        portionWeightG: item.portionWeightG,
        isActive: item.isActive,
        components: rows.map((r, index) => ({
          componentId: r.componentId,
          weightG: r.weightG,
          mandatory: r.mandatory,
          // The database refuses a mandatory part that is not selected, since
          // an item cannot ship without the thing it cannot ship without.
          isDefault: r.mandatory || r.isDefault,
          choiceGroup: r.choiceGroup.trim() || null,
          position: index,
        })),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'items'] })
      await queryClient.invalidateQueries({ queryKey: ['fastfood'] })
      onOpenChange(false)
    },
    onError: (e: Error) => setError(e.message),
  })

  const unused = components.filter((c) => !rows.some((r) => r.componentId === c.id))

  return (
    <Sheet
      open={item != null}
      onOpenChange={onOpenChange}
      title={item?.name ?? ''}
      description="Dalys ir pasirinkimai"
      footer={
        <Button
          variant="primary"
          block
          size="lg"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saugoma…' : 'Išsaugoti'}
        </Button>
      }
    >
      <div className="flex flex-col gap-3 px-4 py-4">
        {rows.length === 0 && <p className="text-sm text-mute">Dalių dar nėra.</p>}

        {rows.map((row, index) => (
          <div
            key={row.componentId}
            className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-line px-3 py-3"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-base text-ink">{nameOf(row.componentId)}</span>
              <button
                type="button"
                className="text-sm text-danger"
                onClick={() => setRows((r) => r.filter((_, i) => i !== index))}
              >
                Šalinti
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumberField
                label="Svoris"
                suffix="g"
                value={row.weightG}
                onChange={(value) =>
                  setRows((r) => r.map((x, i) => (i === index ? { ...x, weightG: value } : x)))
                }
              />
              <Field
                label="Pasirinkimų grupė"
                value={row.choiceGroup}
                placeholder="Pienas, Padažas…"
                onChange={(e) =>
                  setRows((r) =>
                    r.map((x, i) => (i === index ? { ...x, choiceGroup: e.target.value } : x)),
                  )
                }
              />
            </div>
            <div className="flex gap-6">
              <Switch
                label="Privaloma"
                checked={row.mandatory}
                onCheckedChange={(checked) =>
                  setRows((r) => r.map((x, i) => (i === index ? { ...x, mandatory: checked } : x)))
                }
              />
              <Switch
                label="Pažymėta iš pradžių"
                checked={row.mandatory || row.isDefault}
                onCheckedChange={(checked) =>
                  setRows((r) => r.map((x, i) => (i === index ? { ...x, isDefault: checked } : x)))
                }
              />
            </div>
          </div>
        ))}

        <SectionLabel>Pridėti dalį</SectionLabel>
        <select
          value={adding}
          onChange={(e) => {
            const id = Number(e.target.value)
            if (!Number.isFinite(id) || id === 0) return
            setRows((r) => [
              ...r,
              {
                componentId: id,
                weightG: components.find((c) => c.id === id)?.unitWeightG ?? null,
                mandatory: false,
                isDefault: false,
                choiceGroup: '',
                position: r.length,
              },
            ])
            setAdding('')
          }}
          className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-2 text-base text-ink"
        >
          <option value="">Pasirinkite…</option>
          {unused.map((component) => (
            <option key={component.id} value={component.id}>
              {component.name}
            </option>
          ))}
        </select>

        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Sheet>
  )
}
